import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';

import { clearCart, getConfig } from '../../store/modules/cart/actions';
import useHistory from '../../utils/useHistory';

import * as S from './styles';
import Helmet from '../../utils/head';
import Spacer from '../../components/atoms/Spacer';
import Title from '../../components/atoms/Title';
import Subtitle from '../../components/atoms/Subtitle';
import DecorativeDivider from '../../components/atoms/DecorativeDivider'; // eslint-disable-line
import Button from '../../components/atoms/Button';
import Dialog from '../../components/organisms/Dialog';
import InternalHero from '../../Markups/InternalHero';
import Steps from '../../components/molecules/CartSteps';
import Loading from '../../components/molecules/Loading';

import Resume from '../../components/widgets/Cart/Resume';
import Info from '../../components/widgets/Cart/Info';
import Card from '../../components/widgets/Card';
import ResumeCard from '../../components/widgets/Cart/ResumeCard';
import Payment from '../../components/widgets/Payment';
import Confirmation from '../../components/widgets/Cart/Confirmation';

const Cart = () => {
  const [step, setStep] = useState(1);
  const [paymentData, setPaymentData] = useState(null);
  const [forwardButton, setForwardButton] = useState('Próximo passo');
  const [backwardButton, setBackwardButton] = useState('Adicionar presentes');
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    content: '',
    topAction: {
      label: 'OK',
      action: () => {
        setErrorDialog({ ...errorDialog, show: false });
      },
    },
  });
  const history = useHistory();
  const InfoRef = useRef();
  const PaymentRef = useRef();
  const dispatch = useDispatch();
  const { cartStep } = useParams();
  const { profile } = useSelector((state) => state.user);
  const { home } = useSelector((state) => state.menu);
  const { language } = useSelector((state) => state.language);
  const { products, infos, config } = useSelector((state) => state.cart);
  const { showHeader } = useSelector((state) => state.ui);
  const configsLoaded = useMemo(() => {
    return !!Object.keys(config).length;
  }, [config]);

  const returnWeddingDate = useMemo(() => {
    return moment(profile.wedding_datetime)
      .locale(language.htmlTag)
      .format(
        language.htmlTag === 'en' ? 'MMMM DD, YYYY' : 'DD [de] MMMM [de] YYYY'
      );
  }, [language, profile]);

  const handleStepChange = (target, isFromLanguage = false) => {
    if (!isFromLanguage) {
      window.scrollTo({ top: 0 });
    }

    setStep(target);

    switch (target) {
      case 1:
        setForwardButton('Próximo passo');
        setBackwardButton('Adicionar presentes');
        history.replace(`/cart/resume`);
        break;
      case 2:
        setForwardButton('Próximo passo');
        setBackwardButton('Voltar para o carrinho');
        history.replace(`/cart/infos`);
        break;
      case 3:
        setForwardButton('Finalizar compra');
        setBackwardButton('Voltar uma etapa');
        history.replace(`/cart/payment`);
        break;
      case 4:
        dispatch(clearCart());
        setForwardButton('Imprimir página');
        setBackwardButton('Voltar para o site');
        history.replace(`/cart/confirmation`);
        break;
      default:
    }
  };

  const handleForward = async () => {
    let response;
    switch (step) {
      case 1:
        handleStepChange(2);
        break;
      case 2:
        response = InfoRef.current.handleSubmit();
        if (response) handleStepChange(3);
        else handleStepChange(2);
        break;
      case 3:
        setForwardButton('Processando o pagamento...');
        response = await PaymentRef.current.handleSubmit();
        if (response.status === 'success') {
          setPaymentData({
            ...response.message,
            products: response.products,
            card: response.card,
          });
          handleStepChange(4);
        } else {
          setErrorDialog({
            ...errorDialog,
            show: true,
            content: response.message,
          });
          handleStepChange(3);
        }
        break;
      case 4:
        window.print();
        break;
      default:
    }
  };

  const handleBackToHome = () => {
    history.push(`/${home.slug}`);
  };

  const handleBackward = () => {
    switch (step) {
      case 1:
        history.goBack();
        break;
      case 2:
        handleStepChange(1);
        break;
      case 3:
        handleStepChange(2);
        break;
      case 4:
        handleBackToHome();
        break;
      default:
    }
  };

  const getPaymentMethod = useMemo(() => {
    if (paymentData?.payment_method === 'BANK_SLIP') {
      return 'bank-slip';
    }

    if (paymentData?.payment_method === 'PIX') {
      return 'pix';
    }

    return 'credit-card';
  }, [paymentData]);

  useEffect(() => {
    switch (cartStep) {
      case 'resume':
        handleStepChange(1);
        break;
      case 'infos':
        handleStepChange(2);
        break;
      case 'payment':
        if (!Object.keys(infos).length) handleStepChange(2);
        else handleStepChange(3);
        break;
      default:
        if (products.length) history.replace(`/cart/resume`);
        else handleBackward(1);
        break;
    }

    if (!configsLoaded) {
      dispatch(getConfig(profile.couple_id));
    }
  }, []);

  useEffect(() => {
    if (!products.length && step !== 4) handleBackToHome();
  }, [products]);

  return (
    <>
      <Loading loaded={configsLoaded} />
      {configsLoaded && (
        <S.Wrapper additionalMarginTop={showHeader ? 1 : 0}>
          <Helmet pageName="Carrinho" lang={language.htmlTag} />
          {config.internalHero && (
            <S.FixedTop>
              <InternalHero data={config.internalHero.content} />
            </S.FixedTop>
          )}
          {step === 1 ? (
            <>
              {config.internalHero ? (
                <S.FixedTop>
                  <Spacer size="small" />
                </S.FixedTop>
              ) : (
                <>
                  <S.FixedTop>
                    <Spacer size="medium" />
                  </S.FixedTop>
                  <Title className="notranslate">{`${profile.bride_name} & ${profile.groom_name}`}</Title>
                </>
              )}
              <Subtitle>{returnWeddingDate}</Subtitle>
              <DecorativeDivider />
            </>
          ) : (
            <Spacer size="small" />
          )}
          <Steps step={step} />
          {step === 1 && (
            <>
              <Spacer size="small" />
              <Resume />
              <Spacer size="small" />
            </>
          )}
          {step === 2 && (
            <>
              <Subtitle>Preencha seus dados e deixe uma mensagem</Subtitle>
              <Spacer size="small" />
              <Info ref={InfoRef} />
              {config.cardAvailable && (
                <>
                  <Card />
                  <ResumeCard />
                </>
              )}
              <Spacer size="small" />
            </>
          )}
          {step === 3 && (
            <>
              <Subtitle>Escolha uma das formas de pagamento:</Subtitle>
              <Spacer size="small" />
              <Payment ref={PaymentRef} />
              <Spacer size="small" />
            </>
          )}
          {step === 4 && (
            <>
              <Confirmation
                {...paymentData}
                paymentMethod={getPaymentMethod}
                paymentId={
                  paymentData.payment_method === 'BANK_SLIP'
                    ? paymentData.bank_slip_id
                    : paymentData.tid
                }
                date={paymentData.date}
                cardBrand={paymentData.brand}
                bankSlipLink={paymentData.bank_slip_url}
                paymentInfo={paymentData.payment_params}
                products={paymentData.products}
                card={paymentData.card}
              />
              <Spacer size="small" />
            </>
          )}
          <S.ControlsWrapper>
            <Button
              variant={step !== 4 ? 'secondary' : 'primary'}
              handleClick={handleBackward}
            >
              {backwardButton}
            </Button>
            <Button
              data-testid="NextButton"
              handleClick={handleForward}
              variant={step === 4 ? 'secondary' : 'primary'}
              disabled={forwardButton === 'Processando o pagamento...'}
            >
              {forwardButton}
            </Button>
          </S.ControlsWrapper>
          <Spacer size="small" />
          {errorDialog.show && (
            <Dialog
              contentHtml={errorDialog.content}
              topAction={errorDialog.topAction}
            />
          )}
        </S.Wrapper>
      )}
    </>
  );
};

Cart.propTypes = {
  match: PropTypes.object.isRequired,
};

export default Cart;
